import { RecurrenceRule } from "../../../utils/types";
import { ProviderCredential } from "../../clients/types/clients.types";
import {
    NoteCategory,
    SessionServiceType,
} from "../../notes/types/notes.types";
import { ISuperviseeTask } from "../../tasks/types/tasks.types";

export enum ProviderStatus {
    ACTIVE = "active",
    INACTIVE = "inactive",
}

export enum ProviderRole {
    SUPERVISOR = "supervisor",
    SUPERVISEE = "supervisee",
}

export interface IProviderCaseload {
    client: {
        first_name: string;
        last_name: string;
        pronouns: string;
        age: number;
        email: string;
        preferred_name: string;
        contact: string;
        client_id: string;
        insurance_provider_name?: string;
        self_pay?: boolean;
        is_paused: boolean;
        pending_task: boolean;
        intake_client_id: string | null;
        accepts_assignment: boolean;
        client_status: string;
    };
    appointments: {
        session_day: string;
        session_start_hr: number;
        session_start_min: number;
        session_end_hr: number;
        session_end_min: number;
        recurrence_rule: RecurrenceRule;
        session_type: SessionServiceType;
        session_history_id?: string;
        external_session_id?: string;
        therapy_location: string;
        pending_supervisor_intake_note_task: boolean;
        first_session_date?: string;
    }[];
}

export interface IProviderSuperviseeCaseloadDashboard {
    first_name: string;
    last_name: string;
    pronouns: string;
    age: number;
    email: string;
    preferred_name: string;
    contact: string;
    client_id: string;
    insurance_provider_name?: string;
    self_pay?: boolean;
    is_paused: boolean;
    pending_task: boolean;
    intake_client_id: string | null;
    session_day: string;
    session_start_hr: number;
    session_start_min: number;
    session_end_hr: number;
    session_end_min: number;
    recurrence_rule: RecurrenceRule;
    session_type: SessionServiceType;
    session_history_id?: string;
    external_session_id?: string;
    therapy_location: string;
    pending_supervisor_intake_note_task: boolean;
    accepts_assignment: boolean;
    client_status: string;
    provider: {
        provider_id: string;
        first_name: string;
        last_name: string;
    };
    first_session_date?: string;
    tasks: ISuperviseeTask[];
    initial_diagnostic_evaluation_task: ISuperviseeTask | null;
}

export interface IProviderCaseloadDashboard {
    first_name: string;
    last_name: string;
    pronouns: string;
    age: number;
    email: string;
    preferred_name: string;
    contact: string;
    client_id: string;
    insurance_provider_name?: string;
    self_pay?: boolean;
    is_paused: boolean;
    pending_task: boolean;
    intake_client_id: string | null;
    session_day: string;
    session_start_hr: number;
    session_start_min: number;
    session_end_hr: number;
    session_end_min: number;
    recurrence_rule: RecurrenceRule;
    session_type: SessionServiceType;
    session_history_id?: string;
    external_session_id?: string;
    therapy_location: string;
    pending_supervisor_intake_note_task: boolean;
    accepts_assignment: boolean;
    client_status: string;
    first_session_date?: string;
}

export interface IProviderCaseloadFilters {
    providerId: string;
    search_string?: string;
    show_paused_client?: string;
    show_clients_with_pending_tasks?: string;
    show_accepted_assignment_clients?: string;
    enabled?: boolean;
}

export interface ISuperviseeCaseload {
    provider_id: string;
    user_id: string;
    first_name: string;
    last_name: string;
    pronouns: string;
    credential: ProviderCredential;
    email: string;
    preferred_name: string;
    contact: string;
    appointments: number;
    active_clients: number;
    paused_clients: number;
    delivery_option: string[];
    pending_notes: number;
    npi?: string;
    client_appointments: {
        client: {
            first_name: string;
            last_name: string;
            pronouns: string;
            age: number;
            email: string;
            preferred_name: string;
            contact: string;
            client_id: string;
            insurance_provider_name?: string;
            self_pay?: boolean;
            is_paused: boolean;
            pending_task: boolean;
            intake_client_id: string | null;
            accepts_assignment: boolean;
            client_status: string;
            initial_diagnostic_evaluation_task: ISuperviseeTask | null;
        };
        appointments: {
            session_day: string;
            session_start_hr: number;
            session_start_min: number;
            session_end_hr: number;
            session_end_min: number;
            recurrence_rule: RecurrenceRule;
            session_type: SessionServiceType;
            session_history_id?: string;
            external_session_id?: string;
            therapy_location: string;
            pending_supervisor_intake_note_task: boolean;
            tasks: ISuperviseeTask[];
        }[];
    }[];
}

export interface ISuperviseeCaseloadFilters {
    providerId: string;
    search_string?: string;
    show_accepted_assignment_clients?: string;
    show_paused_client?: string;
    show_clients_with_pending_tasks?: string;
    include_inactive_providers?: string;
}

export interface IProvider {
    provider_id: string;
    supervisor_id: string;
    email: string;
    provider_contact_number: string;
    no_of_active_clients: number;
    no_of_supervisees: number;
    is_supervisor: boolean;
    provider_name: string;
    status: ProviderStatus;
    credential: ProviderCredential;
    started_practicing?: string;
    supervisor_name: string | null;
    user_id: string;
    npi?: string;
    supervisor_overrides?: {
        supervisor_id: string;
        note_category: NoteCategory;
        insurance_provider_ids: string[] | null;
        billing_only: boolean;
        insurance_providers:
            | {
                  insurance_provider_id: string;
                  name: string;
              }[]
            | null;
        supervisor: {
            provider_id: string;
            credential: ProviderCredential;
            user: {
                user_id: string;
                first_name: string;
                last_name: string;
                email: string;
                contact: string;
            };
        };
    }[];
}

export interface IProvidersFilterTypes {
    page?: number;
    search_string?: string;
    status?: string;
    credential?: string;
    role?: string;
    limit?: number;
    provider_id?: string;
    provider_ids?: string[];
    sort_attribute?: string;
    sort_order?: string;
}

export interface IActiveProvider {
    provider_id: string;
    supervisor_id: string;
    is_supervisor: boolean;
    provider_name: string;
    status: ProviderStatus;
    credential: ProviderCredential;
    started_practicing: string;
}

export interface IEditProviderInfo {
    npi?: string;
    is_supervisor?: boolean;
    credential?: string;
    started_practicing?: string;
    status?: string;
    supervisor_id?: string;
    note_category?: string;
}

export interface IPauseClientPayload {
    client_ids: string[];
    action_type: "pause" | "unpause";
}

export interface IProviderUpcomingSessionsFilters {
    from_date: string;
    to_date: string;
}

export interface IProviderUpcomingSession {
    session_id: string;
    session_history_id: string;
    provider_id: string;
    start_date_time: string;
    end_date_time: string;
    session_start_hr: number;
    session_start_min: number;
    session_end_hr: number;
    session_end_min: number;
    service: SessionServiceType;
    status: ProviderStatus;
    delivery_option: string;
    is_paid: boolean;
    session_day: string;
    recurrence_rule: RecurrenceRule;
    is_cancelled: boolean;
    client: {
        client_id: string;
        first_name: string;
        last_name: string;
        pronouns: string;
        preffered_name: string;
        email: string;
        insurance_provider_name: string | null;
        self_pay: boolean | null;
    };
}

export interface ProviderSupervisorOverrideItem {
    note_category: string;
    supervisor_id: string | null;
    insurance_provider_ids?: string[] | null;
    billing_only: boolean;
}

export interface IOverrideProviderSupervisorPayload {
    overrides?: ProviderSupervisorOverrideItem[];
}

export type IOverrideProviderSupervisorResponse = {
    override_id: string;
    provider_id: string;
    supervisor_id: string;
    note_category: NoteCategory;
    insurance_provider_ids: string[] | null;
    billing_only: boolean;
    created_at: string;
    updated_at: string;
}[];
